import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrg } from '../../hooks/useOrg';
import { useNotifications } from '../NotificationSystem';
import { atpSchedulerService } from '../../services/atpSchedulerService';
import { userGroupService } from '../../services/userGroupService';
import authService from '../../services/authService';
import ATPSchedulerRunModal from '../sampling/ATPSchedulerRunModal';
import './ATPSchedulerSettings.css';

const DEFAULT_FREQUENCY = { critical: 1, high: 3, medium: 7, low: 14 };

const ATPSchedulerSettings = () => {
  const { t, i18n } = useTranslation('ipc');
  const { activeOrgId } = useOrg();
  const { notify } = useNotifications();
  const currentUser = authService.getCurrentUser();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [lastRunInfo, setLastRunInfo] = useState(null);

  const [config, setConfig] = useState({
    is_active: false,
    assigned_group_id: '',
    assigned_group_name: '',
    assigned_user_ids: [],
    assigned_user_names: [],
    frequency: { ...DEFAULT_FREQUENCY },
    randomization_window_percent: 20
  });

  useEffect(() => {
    if (!activeOrgId) return;
    setLoading(true);
    Promise.all([
      atpSchedulerService.getSchedulerConfig(activeOrgId),
      userGroupService.getAllGroups({ isActive: true })
    ]).then(([cfg, grps]) => {
      if (cfg) {
        setConfig({
          is_active: cfg.is_active || false,
          assigned_group_id: cfg.assigned_group_id || '',
          assigned_group_name: cfg.assigned_group_name || '',
          assigned_user_ids: cfg.assigned_user_ids || [],
          assigned_user_names: [],
          frequency: { ...DEFAULT_FREQUENCY, ...(cfg.frequency || {}) },
          randomization_window_percent: cfg.randomization_window_percent || 20
        });
        setLastRunInfo({ last_run_at: cfg.last_run_at, next_run_at: cfg.next_run_at });
        if (cfg.assigned_group_id) {
          loadGroupMembers(cfg.assigned_group_id, cfg.assigned_user_ids || []);
        }
      }
      setGroups(grps.filter(g => !g.organizationId || g.organizationId === activeOrgId));
    }).catch(err => notify.error(err.message))
      .finally(() => setLoading(false));
  }, [activeOrgId]);

  const loadGroupMembers = async (groupId, selectedUserIds = []) => {
    try {
      const group = await userGroupService.getGroup(groupId);
      const members = (group?.members || []).map(m => ({
        userId: m.userId || m.id,
        fullName: m.name || m.fullName || m.displayName || m.userId || m.id
      }));
      setGroupMembers(members);
      const names = selectedUserIds.map(uid => {
        const m = members.find(mb => mb.userId === uid);
        return m ? m.fullName : uid;
      });
      setConfig(prev => ({ ...prev, assigned_user_names: names }));
    } catch (err) {
      console.warn('[ATPSchedulerSettings] loadGroupMembers:', err);
    }
  };

  const handleGroupChange = async (groupId) => {
    const group = groups.find(g => g.id === groupId);
    setConfig(prev => ({
      ...prev,
      assigned_group_id: groupId,
      assigned_group_name: group?.name || '',
      assigned_user_ids: [],
      assigned_user_names: []
    }));
    setGroupMembers([]);
    setMemberSearchTerm('');
    if (groupId) await loadGroupMembers(groupId, []);
  };

  const handleAddMember = (member) => {
    if (config.assigned_user_ids.includes(member.userId)) return;
    setConfig(prev => ({
      ...prev,
      assigned_user_ids: [...prev.assigned_user_ids, member.userId],
      assigned_user_names: [...prev.assigned_user_names, member.fullName]
    }));
    setMemberSearchTerm('');
  };

  const handleRemoveMember = (userId) => {
    const idx = config.assigned_user_ids.indexOf(userId);
    setConfig(prev => ({
      ...prev,
      assigned_user_ids: prev.assigned_user_ids.filter(id => id !== userId),
      assigned_user_names: prev.assigned_user_names.filter((_, i) => i !== idx)
    }));
  };

  const handleFrequencyChange = (level, value) => {
    setConfig(prev => ({
      ...prev,
      frequency: { ...prev.frequency, [level]: Number(value) || 1 }
    }));
  };

  const handleSave = async () => {
    if (!config.assigned_group_id) {
      notify.error(t('atpScheduler.selectGroupError'));
      return;
    }
    setSaving(true);
    try {
      await atpSchedulerService.saveSchedulerConfig(activeOrgId, config, currentUser?.uid);
      notify.success(t('atpScheduler.settingsSaved'));
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatTs = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const loc = i18n.language?.startsWith('ro') ? 'ro-RO' : 'en-GB';
    return d.toLocaleDateString(loc, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredMembers = groupMembers.filter(m => {
    const term = memberSearchTerm.toLowerCase();
    return m.fullName.toLowerCase().includes(term) &&
      !config.assigned_user_ids.includes(m.userId);
  });

  if (loading) return <div className="atp-scheduler-settings">{t('atpScheduler.loading')}</div>;

  return (
    <div className="atp-scheduler-settings">
      <h3>{t('atpScheduler.pageTitle')}</h3>
      <p className="section-desc">{t('atpScheduler.pageDescription')}</p>

      <div className="form-field toggle-field">
        <label className="toggle-label">
          <input
            type="checkbox"
            checked={config.is_active}
            onChange={e => setConfig(prev => ({ ...prev, is_active: e.target.checked }))}
          />
          <span>{t('atpScheduler.schedulerActive')}</span>
        </label>
        {!config.is_active && (
          <p className="field-hint">{t('atpScheduler.hintInactive')}</p>
        )}
      </div>

      <div className="form-field">
        <label>{t('atpScheduler.groupLabel')} <span className="required">*</span></label>
        <select
          value={config.assigned_group_id}
          onChange={e => handleGroupChange(e.target.value)}
        >
          <option value="">{t('atpScheduler.selectGroup')}</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>

      {config.assigned_group_id && (
        <div className="form-field">
          <label>
            {t('atpScheduler.membersOptional')}
          </label>

          {config.assigned_user_ids.length > 0 && (
            <div className="recipient-chips">
              {config.assigned_user_ids.map((uid, idx) => (
                <span key={uid} className="recipient-chip">
                  {config.assigned_user_names[idx] || uid}
                  <button
                    type="button"
                    className="chip-remove"
                    onClick={() => handleRemoveMember(uid)}
                  >×</button>
                </span>
              ))}
            </div>
          )}

          <input
            type="text"
            className="user-search-input"
            placeholder={t('atpScheduler.searchByName')}
            value={memberSearchTerm}
            onChange={e => setMemberSearchTerm(e.target.value)}
          />

          {memberSearchTerm.trim().length > 0 && (
            <div className="user-search-results">
              {filteredMembers.length === 0 && (
                <div className="user-search-empty">{t('atpScheduler.noMemberMatch')}</div>
              )}
              {filteredMembers.slice(0, 8).map(m => (
                <div
                  key={m.userId}
                  className="user-search-item"
                  onClick={() => handleAddMember(m)}
                >
                  <span className="user-search-name">{m.fullName}</span>
                </div>
              ))}
            </div>
          )}

          {groupMembers.length === 0 && !memberSearchTerm && (
            <p className="field-hint">{t('atpScheduler.groupNoMembers')}</p>
          )}
        </div>
      )}

      <div className="form-field">
        <label>{t('atpScheduler.frequencyLabel')}</label>
        <div className="frequency-grid">
          {[
            { key: 'critical', label: t('critical') },
            { key: 'high', label: t('high') },
            { key: 'medium', label: t('medium') },
            { key: 'low', label: t('low') }
          ].map(({ key, label }) => (
            <div key={key} className="frequency-item">
              <label>{label}</label>
              <div className="frequency-input-row">
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={config.frequency[key]}
                  onChange={e => handleFrequencyChange(key, e.target.value)}
                />
                <span className="frequency-unit">{t('atpScheduler.daysUnit')}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="form-field">
        <label>{t('atpScheduler.randomizationLabel')}</label>
        <div className="randomization-row">
          <input
            type="number"
            min={0}
            max={50}
            value={config.randomization_window_percent}
            onChange={e => setConfig(prev => ({
              ...prev,
              randomization_window_percent: Number(e.target.value) || 0
            }))}
            style={{ maxWidth: 80 }}
          />
          <span className="frequency-unit">%</span>
          <span className="field-hint-inline">
            {t('atpScheduler.randomizationHint', {
              days: Math.round(config.frequency.medium * config.randomization_window_percent / 100),
              medium: config.frequency.medium
            })}
          </span>
        </div>
      </div>

      {lastRunInfo && (
        <div className="run-info">
          <div><span>{t('atpScheduler.lastRun')}:</span> <strong>{formatTs(lastRunInfo.last_run_at)}</strong></div>
          <div><span>{t('atpScheduler.nextRun')}:</span> <strong>{formatTs(lastRunInfo.next_run_at)}</strong></div>
        </div>
      )}

      <div className="scheduler-actions">
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? t('atpScheduler.saving') : t('atpScheduler.saveConfig')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => setShowRunModal(true)}>
          ▶ {t('atpScheduler.runNow')}
        </button>
      </div>

      {showRunModal && activeOrgId && (
        <ATPSchedulerRunModal
          organizationId={activeOrgId}
          onClose={() => setShowRunModal(false)}
          onSuccess={async (result) => {
            notify.success(
              t('atpScheduler.success') +
                ` — ${result.tasks_generated} / ${result.rooms_evaluated}`
            );
            setShowRunModal(false);
            try {
              const updated = await atpSchedulerService.getSchedulerConfig(activeOrgId);
              if (updated) setLastRunInfo({ last_run_at: updated.last_run_at, next_run_at: updated.next_run_at });
            } catch (e) {
              /* non-blocking */
            }
          }}
        />
      )}
    </div>
  );
};

export default ATPSchedulerSettings;
